import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { fixedAssetsApi, type DepreciationPreviewResponse, type DepreciationRunHistoryItem } from '@/api/fixedAssetsApi';
import { cardCls, errMsg, fmtDate, inputCls, money, primaryBtn, secondaryBtn, thCls } from '../components/ui';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function DepreciationRunPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const now = new Date();
  // Default to the last completed month
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [preview, setPreview] = useState<DepreciationPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<DepreciationRunHistoryItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const loadRuns = useCallback(() => {
    fixedAssetsApi.getDepreciationRuns().then(setRuns).catch(() => {});
  }, []);

  const loadPreview = useCallback(async () => {
    if (!month || !year) return;
    try {
      setLoading(true);
      setPreviewError(null);
      setPreview(await fixedAssetsApi.previewDepreciation({ month, year }));
    } catch (err) {
      setPreview(null);
      setPreviewError(errMsg(err, 'Failed to calculate depreciation'));
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const post = async () => {
    try {
      setBusy(true);
      const res = await fixedAssetsApi.executeDepreciation({ month, year });
      toast.success(res.message || `Depreciation posted (${res.data.journalVoucherNumber})`);
      loadRuns();
      loadPreview();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to post depreciation'));
    } finally {
      setBusy(false);
    }
  };

  const reverse = (r: DepreciationRunHistoryItem) =>
    setConfirm({
      title: 'Reverse Depreciation Run',
      message: `Reverse the depreciation for ${MONTHS[r.period_month - 1]} ${r.period_year} (${money(r.total_depreciation_amount)})? Asset book values are restored and the voucher is removed.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          setBusy(true);
          const res = await fixedAssetsApi.reverseDepreciation(r.id);
          toast.success(res.message || 'Depreciation run reversed');
          loadRuns();
          loadPreview();
        } catch (err) {
          toast.error(errMsg(err, 'Failed to reverse the run'));
        } finally {
          setBusy(false);
        }
      },
    });

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 4 + i);
  const catchUps = preview?.lines.filter((l) => l.isCatchUp).length || 0;

  return (
    <AppLayout>
      <div className="p-6 max-w-[1300px] mx-auto space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate('/finance/assets')} className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#e2e8f0] bg-white text-slate-500 hover:bg-slate-50 cursor-pointer">
              <i className="ri-arrow-left-line" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1e293b]">Run Depreciation</h1>
              <p className="text-sm text-slate-500 mt-0.5">Monthly book depreciation — posts Dr Depreciation Expense / Cr Accumulated Depreciation</p>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Month</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={`${inputCls} w-36`}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Year</label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={`${inputCls} w-24`}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button onClick={loadPreview} disabled={loading} className={secondaryBtn}>
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} /> Recalculate
            </button>
          </div>
        </div>

        {previewError && <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700">{previewError}</div>}

        {preview && (
          <>
            {preview.alreadyRun ? (
              <div className="p-4 rounded-xl border border-green-200 bg-green-50 text-sm text-green-800 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-lg" />
                Depreciation for {MONTHS[preview.periodMonth - 1]} {preview.periodYear} is already posted. Reverse it below to re-run.
              </div>
            ) : (
              catchUps > 0 && (
                <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 flex items-center gap-2">
                  <i className="ri-information-line text-lg" />
                  {catchUps} asset(s) include earlier unposted months (catch-up) — they are charged from their last depreciated date up to {fmtDate(preview.periodEnd)}.
                </div>
              )
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                ['Period', `${fmtDate(preview.periodStart)} – ${fmtDate(preview.periodEnd)}`],
                ['Financial Year', `FY ${preview.financialYear}`],
                ['Assets', String(preview.totalAssets)],
                ['Total Depreciation', money(preview.totalDepreciation)],
              ].map(([k, v]) => (
                <div key={k} className={`${cardCls} p-4`}>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{k}</p>
                  <p className="text-lg font-bold mt-1 text-[#1e293b]">{v}</p>
                </div>
              ))}
            </div>

            {preview.categorySummaries.length > 0 && (
              <div className={`${cardCls} overflow-x-auto`}>
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
                  <p className="text-sm font-semibold text-slate-800">Journal Summary by Category</p>
                </div>
                <table className="w-full text-xs min-w-[600px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className={`${thCls} text-left`}>Category</th>
                      <th className={`${thCls} text-left`}>Dr (Expense)</th>
                      <th className={`${thCls} text-left`}>Cr (Accumulated Depreciation)</th>
                      <th className={`${thCls} text-right`}>Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.categorySummaries.map((c) => (
                      <tr key={c.categoryName}>
                        <td className="px-4 py-2 font-medium text-[#1e293b]">{c.categoryName}</td>
                        <td className="px-4 py-2 text-slate-600">{c.expenseAccName}</td>
                        <td className="px-4 py-2 text-slate-600">{c.deprAccName}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">{money(c.totalAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className={`${cardCls} overflow-x-auto`}>
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">Asset-wise Calculation</p>
                {!preview.alreadyRun && (
                  <button onClick={post} disabled={busy || preview.totalDepreciation <= 0} className={primaryBtn}>
                    <i className="ri-check-double-line" /> Post Depreciation ({money(preview.totalDepreciation)})
                  </button>
                )}
              </div>
              <table className="w-full text-xs min-w-[900px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className={`${thCls} text-left`}>Asset</th>
                    <th className={`${thCls} text-left`}>Category</th>
                    <th className={`${thCls} text-left`}>Method</th>
                    <th className={`${thCls} text-left`}>Period</th>
                    <th className={`${thCls} text-right`}>Opening BV</th>
                    <th className={`${thCls} text-right`}>Depreciation</th>
                    <th className={`${thCls} text-right`}>Closing BV</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.lines.map((l) => (
                    <tr key={l.assetId} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/finance/assets/${l.assetId}`)}>
                      <td className="px-4 py-2">
                        <p className="font-medium text-[#1e293b]">{l.assetName}</p>
                        <p className="text-[10px] font-mono text-slate-400">{l.assetCode}</p>
                      </td>
                      <td className="px-4 py-2 text-slate-600">{l.categoryName}</td>
                      <td className="px-4 py-2 text-slate-600">
                        {l.method === 'SLM' ? `SLM · ${l.usefulLifeYears ?? '—'} yrs` : `WDV · ${l.depreciationRate}%`}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {fmtDate(l.periodFrom)} – {fmtDate(l.periodTo)}
                        {l.isCatchUp && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">Catch-up</span>}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">{money(l.openingBookValue)}</td>
                      <td className="px-4 py-2 text-right font-mono text-red-600 font-semibold">{money(l.depreciationAmount)}</td>
                      <td className="px-4 py-2 text-right font-mono">{money(l.closingBookValue)}</td>
                    </tr>
                  ))}
                  {preview.lines.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                        {preview.alreadyRun ? 'Already posted for this month.' : 'No assets are due for depreciation in this period.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className={`${cardCls} overflow-x-auto`}>
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <p className="text-sm font-semibold text-slate-800">Posted Runs</p>
            <p className="text-xs text-slate-400">Only the latest run can be reversed</p>
          </div>
          <table className="w-full text-xs min-w-[800px]">
            <thead className="bg-slate-50">
              <tr>
                <th className={`${thCls} text-left`}>Period</th>
                <th className={`${thCls} text-left`}>FY</th>
                <th className={`${thCls} text-right`}>Assets</th>
                <th className={`${thCls} text-right`}>Depreciation</th>
                <th className={`${thCls} text-left`}>Voucher</th>
                <th className={`${thCls} text-left`}>Posted</th>
                <th className={thCls} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.map((r, i) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 font-medium text-[#1e293b]">
                    {MONTHS[r.period_month - 1]} {r.period_year}
                  </td>
                  <td className="px-4 py-2">FY {r.financial_year}</td>
                  <td className="px-4 py-2 text-right">{r.total_assets_processed}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{money(r.total_depreciation_amount)}</td>
                  <td className="px-4 py-2 font-mono">
                    {r.jv_id ? (
                      <button onClick={() => navigate(`/finance/journal/${r.jv_id}`)} className="text-[#4f46e5] hover:underline cursor-pointer">
                        {r.jv_number}
                      </button>
                    ) : (
                      r.jv_number || '—'
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500">
                    {fmtDate(r.run_date)}
                    {r.run_by_email ? ` · ${r.run_by_email}` : ''}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {i === 0 && (
                      <button onClick={() => reverse(r)} disabled={busy} className="text-xs font-medium text-red-600 hover:underline cursor-pointer disabled:opacity-50">
                        Reverse
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    No depreciation runs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        variant="warning"
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </AppLayout>
  );
}
